import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Service } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Button } from '@/components/generals/button'
import { Process } from '@/components/sections/process'
import { Benefit } from '@/components/sections/benefit'
import { Faq } from '@/components/sections/faq'
import { Cta } from '@/components/sections/cta'
import { getService, getServiceSlugs } from '@/lib/content'

type ServiceDetailProps = {
    service: Service
}

const ServiceDetail: FC<ServiceDetailProps> = ({ service }): ReactNode => {
    return (
        <Layout>
            <Seo title={service.title} description={service.heroSubtitle} ogImage={service.cardImage} />

            <section className='hero-section section-padding'>
                <div className='w-layout-blockcontainer hero-container w-container'>
                    <div className='service-details-hero'>
                        <div className='hero-title-wrapper align-center'>
                            <h1
                                className='hero-title'
                                data-hero-reveal
                                data-mdlfy-cms={`services:${service.id}:title`}
                            >
                                {service.title}
                            </h1>

                            <p
                                className='hero-subtitle service-details'
                                data-hero-reveal
                                data-mdlfy-cms={`services:${service.id}:hero_subtitle`}
                            >
                                {service.heroSubtitle}
                            </p>
                        </div>

                        <div className='service-details-hero-bottom' data-hero-reveal>
                            <Button href='/book-free-consultation' label='Get Free Estimate' />

                            <div className='service-details-rating-wrapper'>
                                {service.ratingPrimary ? (
                                    <div className='hero-rating-block style-02'>
                                        <img
                                            src='/images/ng/google-g.svg'
                                            width={22}
                                            height={22}
                                            loading='lazy'
                                            alt='Google'
                                        />

                                        <p
                                            className='hero-rating-number style-02'
                                            data-mdlfy-cms={`services:${service.id}:rating_primary`}
                                        >
                                            {service.ratingPrimary}
                                        </p>

                                        <p
                                            className='hero-rating-text style-02'
                                            data-mdlfy-cms={`services:${service.id}:rating_primary_count`}
                                        >
                                            {service.ratingPrimaryCount}
                                        </p>
                                    </div>
                                ) : null}

                                {service.ratingSecondary ? (
                                    <div className='hero-rating-block style-02'>
                                        <img src='/images/frame-2147237995.svg' loading='lazy' alt='Houzz' />

                                        <p
                                            className='hero-rating-number style-02'
                                            data-mdlfy-cms={`services:${service.id}:rating_secondary`}
                                        >
                                            {service.ratingSecondary}
                                        </p>

                                        <p
                                            className='hero-rating-text style-02'
                                            data-mdlfy-cms={`services:${service.id}:rating_secondary_count`}
                                        >
                                            {service.ratingSecondaryCount}
                                        </p>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className='service-details-section padding-bottom'>
                <div className='w-layout-blockcontainer hero-container w-container'>
                    <div className='service-details-content-wrapper'>
                        <img
                            src={service.cardImage}
                            loading='lazy'
                            alt={service.cardImageAlt}
                            className='single-service-thumbnail'
                            data-reveal='image'
                        />

                        <div className='single-service-content w-richtext' data-reveal>
                            <p>
                                <strong data-mdlfy-cms={`services:${service.id}:intro`}>
                                    {service.intro}
                                </strong>
                            </p>

                            <h2 data-mdlfy-cms={`services:${service.id}:included_heading`}>
                                {service.includedHeading}
                            </h2>

                            <div dangerouslySetInnerHTML={{ __html: service.body }} />
                        </div>

                        <div className='service-feature-card-wrapper' data-reveal-stagger='0.1'>
                            {service.features.map((feature, index) => (
                                <div key={feature.id} className='single-service-feature-card'>
                                    <div className='service-feature-number-box'>
                                        <p className='service-feature-card-number'>
                                            {index + 1}
                                        </p>
                                    </div>

                                    <div className='service-feature-card-title-wrapper'>
                                        <p
                                            className='service-feature-card-title'
                                            data-mdlfy-cms={`service_features:${feature.id}:title`}
                                        >
                                            {feature.title}
                                        </p>

                                        <p
                                            className='service-feature-card-content'
                                            data-mdlfy-cms={`service_features:${feature.id}:content`}
                                        >
                                            {feature.content}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <Process variant='details' />
            <Benefit variant='details' />
            <Faq />
            <Cta />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getServiceSlugs()

    return {
        paths: slugs.map((slug) => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<ServiceDetailProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const service = await getService(slug)

    if (!service) {
        return { notFound: true, revalidate: 60 }
    }

    return {
        props: { service },
        revalidate: 60
    }
}

export default ServiceDetail
